import { ref, customRef, type Ref } from 'vue'

type LazyRef<T> = Ref<T> & {
  load(): Promise<T>
  reload(): Promise<T>
  isLoading: Ref<boolean>
  isLoaded: Ref<boolean>
}

type Loader<T> = () => Promise<T>

/**
 * Lazy-loaded ref
 *
 * Similar to computedAsync(), but the dependencies are not tracked.
 */
export const lazyRef = <T>(loader: Loader<T>, initialData: T): LazyRef<T> => {
  let value: T = initialData
  let lazyRefTrigger: () => void
  let loadPromise: Promise<T> | undefined
  const isLoading = ref(false)
  const isLoaded = ref(false)

  const load = (): Promise<T> => {
    return (loadPromise = Promise.resolve()
      .then(() => (isLoading.value = true))
      .then(loader)
      .then((loadedValue) => {
        value = loadedValue
        isLoaded.value = true
        lazyRefTrigger()

        return value
      })
      .finally(() => {
        isLoading.value = false
      }))
  }

  const lazyRefObj = customRef((track, trigger) => {
    lazyRefTrigger = trigger

    return {
      get() {
        track()

        // trigger load on first access
        if (loadPromise === undefined) {
          void load()
        }

        return value
      },
      set() {
        throw new Error('Cannot assign to lazyRef')
      },
    }
  }) as LazyRef<T>

  lazyRefObj.load = () => loadPromise ?? load()
  lazyRefObj.reload = load
  lazyRefObj.isLoading = isLoading
  lazyRefObj.isLoaded = isLoaded

  return lazyRefObj
}
